import { com } from '@eidu/web'
import React, { useState } from 'react'
import { Button, Card, Group, Stack } from '@common/ui'
import EntityTypeField from '../../pages/permission/path/EntityTypeField'
import { RenderablePath } from '../../pages/permission/path/PathField'
import ComplexPathField from './ComplexPathField'
import useRenderablePaths from '../misc/useRenderablePaths'
import { useCreateSemanticLink } from '../../io/useSemanticLinks'
import SemanticLinkConfig from '../../types/SemanticLinkConfig'
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntityPath = com.eidu.entity.path.EntityPath

type SemanticLinkFieldProps = {
  entityType: EntityTypeId
  entityTypes: readonly EntityType[]
  semanticLinkConfig: SemanticLinkConfig
  onSave: () => void
  onCancel: () => void
}

const SemanticLinkField = ({
  entityType,
  entityTypes,
  semanticLinkConfig,
  onSave,
  onCancel,
}: SemanticLinkFieldProps) => {
  const availableTargetEntityTypes = entityTypes.filter(({ kind }) =>
    semanticLinkConfig.toTypeKind ? kind.equals(semanticLinkConfig.toTypeKind) : true
  )
  const [selectedTargetTypeId, setSelectedTargetTypeId] = useState(availableTargetEntityTypes.at(0)?.id)
  const [selectedPath, setSelectedPath] = useState<EntityPath>()
  const { save, isSaving } = useCreateSemanticLink(onSave)

  const targetType =
    selectedTargetTypeId && entityTypes.find(({ id: entityTypeId }) => entityTypeId === selectedTargetTypeId)

  const renderablePaths: RenderablePath[] = useRenderablePaths(entityType, targetType, entityTypes)

  const handleSave = () => {
    if (selectedPath) save(semanticLinkConfig.key, selectedPath)
  }

  return (
    <Card withBorder>
      <Stack gap="md">
        <EntityTypeField
          entityTypeId={selectedTargetTypeId}
          onChange={(type) => {
            setSelectedTargetTypeId(type.id)
            setSelectedPath(undefined)
          }}
          types={availableTargetEntityTypes}
          label="Target type"
        />
        <ComplexPathField
          entityTypeId={entityType}
          targetType={targetType}
          selectedPath={selectedPath}
          setSelectedPath={setSelectedPath}
          renderablePaths={renderablePaths}
          fieldType={semanticLinkConfig.fieldType}
        />
        <Group gap="md" justify="flex-end">
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !selectedPath}>
            Save
          </Button>
        </Group>
      </Stack>
    </Card>
  )
}

export default SemanticLinkField
